import React from 'react';
import { Control, Controller } from 'react-hook-form';
import { FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RemovableChip from '../../custom-components/RemovableChip';
import { SelectProps } from '@material-ui/core/Select';

type Option<T = string> = {
  value: T;
  label: React.ReactNode;
  disabled?: boolean;
};

export type FormSelectProps<T = string> = Omit<
  SelectProps,
  'name' | 'value' | 'onChange' | 'multiple' | 'label' | 'margin'
> & {
  name: string;
  control: Control<any>;
  label?: React.ReactNode;
  options: Option<T>[];
  multiple?: boolean;
  /**
   * With `multiple`, shows each selection as a removable pill inside the field, which grows to
   * however many rows they need. Takes precedence over any `renderValue` passed in.
   */
  chips?: boolean;
  helperText?: React.ReactNode;
  formControlProps?: Omit<React.ComponentProps<typeof FormControl>, 'onChange' | 'component'>;
};

const useStyles = makeStyles(() => ({
  /**
   * Lets the field grow with its pills instead of clipping them to MUI's one fixed-height line.
   * The vertical padding is halved from the outlined input's 18.5px because the pills carry their
   * own height, and the right padding keeps the last one clear of the arrow.
   */
  chipInput: {
    height: 'auto',
    minHeight: 21,
    whiteSpace: 'normal',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    padding: '9px 32px 9px 14px',
  },
  placeholder: {
    color: '#949494', // Gray 400
  },
}));

function stringKey(v: unknown) {
  return String(v);
}

const FormSelect = React.forwardRef<HTMLDivElement, FormSelectProps<any>>(
  (
    {
      name,
      control,
      label,
      options,
      multiple = false,
      chips = false,
      helperText,
      formControlProps,
      variant = 'outlined',
      fullWidth = true,
      disabled,
      ...selectProps
    },
    ref,
  ) => {
    const classes = useStyles();
    const labelId = `${name}-label`;
    const selectId = `${name}-select`;

    const valueMap = React.useMemo(() => {
      const m = new Map<string, any>();
      options.forEach((o) => m.set(stringKey(o.value), o.value));
      return m;
    }, [options]);

    return (
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          const hasError = !!fieldState.error;
          const isDisabled = disabled || field.disabled;

          const normalizedValue = multiple
            ? Array.isArray(field.value)
              ? (field.value as any[]).map(stringKey)
              : []
            : field.value == null
            ? ''
            : stringKey(field.value);

          // Each pill is wrapped in a mousedown-swallowing span: MUI opens the menu on mousedown,
          // so without it removing a pill would also drop the list open over the field.
          const renderChips = (selected: unknown) => {
            const values = (selected as string[]) ?? [];
            if (values.length === 0) return null;
            return values.map((value) => (
              <span key={value} onMouseDown={(event) => event.stopPropagation()}>
                <RemovableChip
                  label={value}
                  onRemove={() => field.onChange(values.filter((v) => v !== value))}
                />
              </span>
            ));
          };

          const useChips = multiple && chips;

          return (
            <FormControl
              size="small"
              variant={variant}
              fullWidth={fullWidth}
              error={hasError}
              disabled={isDisabled}
              {...formControlProps}
            >
              {label && <InputLabel id={labelId}>{label}</InputLabel>}

              <Select
                {...selectProps}
                renderValue={useChips ? renderChips : selectProps.renderValue}
                classes={
                  useChips
                    ? { ...selectProps.classes, select: classes.chipInput }
                    : selectProps.classes
                }
                ref={ref}
                labelId={label ? labelId : undefined}
                id={selectId}
                value={normalizedValue}
                multiple={multiple}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (multiple) {
                    const arr = (raw as string[] | string) ?? [];
                    const values = Array.isArray(arr) ? arr : [arr];
                    field.onChange(values.map((k) => valueMap.get(k) ?? k));
                  } else {
                    const key = raw as string;
                    field.onChange(valueMap.get(key) ?? key);
                  }
                }}
                onBlur={field.onBlur}
                inputProps={{ name: field.name }}
                label={label}
              >
                {options.map((opt) => (
                  <MenuItem
                    key={stringKey(opt.value)}
                    value={stringKey(opt.value)}
                    disabled={opt.disabled}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }}
      />
    );
  },
);

export default FormSelect;
